import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const HEADER_IMG = '/bracketsim_header.png';

const steps = [
  {
    title: 'Import Your Pool',
    desc: 'Import your pool directly from ESPN, CBS, or Yahoo bracket games, or enter picks manually. All player brackets load in seconds.',
  },
  {
    title: 'Build Scenarios',
    desc: 'Set up what-if tournament outcomes. What happens if Duke wins it all? What if your champion gets bounced in the first round? Test any scenario.',
  },
  {
    title: 'Run Simulations',
    desc: 'The engine models thousands of possible outcomes — out of 9.2 quintillion total — using live odds to calculate each player\'s probability of winning your pool.',
  },
  {
    title: 'Find Your Rooting Interests',
    desc: 'Get win probabilities for every player and see which upcoming games matter most for your bracket.',
  },
];

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'How It Works — My Bracket Sim',
    description: 'Learn how My Bracket Sim works. Import your pool, build scenarios, run simulations, and find your rooting interests.',
    path: '/how-it-works',
  });

  return (
    <div style={s.page}>
      <header style={s.header}>
        <img
          src={HEADER_IMG}
          alt="BracketSim"
          style={s.logo}
          onClick={() => navigate('/')}
        />
        <nav style={s.nav}>
          <a href="/pricing" style={s.navLink}>Pricing</a>
          <a href="/login" className="hiw-login-link" style={s.navAccent}>Log In</a>
        </nav>
      </header>

      <section style={s.hero}>
        <div style={s.heroInner}>
          <span style={s.label}>{'{ '}HOW IT WORKS{' }'}</span>
          <h1 style={s.title}>
            Import your pool. Run the numbers.
            <br />
            Know exactly where you stand.
          </h1>
        </div>
      </section>

      <section style={s.steps}>
        <div style={s.stepsGrid}>
          {steps.map((step, i) => (
            <div key={i} className="hiw-step" style={s.step}>
              <div style={s.stepTop}>
                <span style={s.stepNum}>{String(i + 1).padStart(2, '0')}</span>
                <div style={s.stepLine} />
              </div>
              <h3 style={s.stepTitle}>{step.title}</h3>
              <p style={s.stepDesc}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={s.tech}>
        <div style={s.techBox}>
          <span style={s.techLabel}>{'{ '}THE MATH{' }'}</span>
          <p style={s.techText}>
            The simulator uses Monte Carlo simulation to model thousands of possible
            tournament outcomes. Each run uses live real-time odds to determine game-by-game
            probabilities, then scores every player's bracket against each outcome.
          </p>
          <p style={{ ...s.techText, marginBottom: 0 }}>
            After aggregating results, you get precise win probabilities for every player
            in your pool. The rooting interest engine then pinpoints which upcoming games
            swing your standing the most.
          </p>
        </div>
      </section>

      <section style={s.cta}>
        <h2 style={s.ctaTitle}>Run your first simulation</h2>
        <button
          style={s.ctaBtn}
          className="hiw-cta"
          onClick={() => navigate('/login')}
        >
          Get Started — Free
        </button>
      </section>

      <footer style={s.footer}>
        <p style={s.footerRow}>
          <a href="/privacy" className="hiw-flink">Privacy Policy</a>
          <span style={s.dot}>&middot;</span>
          <a href="/contact" className="hiw-flink">Contact</a>
        </p>
        <p style={{ marginBottom: '12px' }}>
          <a href="mailto:hello@mybracketsim.com" className="hiw-flink">
            hello@mybracketsim.com
          </a>
        </p>
        <p style={s.copy}>&copy; 2026 MyBracketSim</p>
      </footer>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#faf8f5',
    fontFamily: '"Outfit", sans-serif',
    color: '#1c1917',
  },

  /* Header */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 8%',
    borderBottom: '1px solid #e7e5e4',
  },
  logo: { height: '36px', cursor: 'pointer' },
  nav: { display: 'flex', gap: '24px', alignItems: 'center' },
  navLink: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#1c1917',
    textDecoration: 'none',
    letterSpacing: '0.03em',
  },
  navAccent: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#e8622d',
    textDecoration: 'none',
    letterSpacing: '0.03em',
    padding: '7px 18px',
    border: '1.5px solid #e8622d',
    borderRadius: '3px',
  },

  /* Hero */
  hero: {
    padding: '90px 8% 50px',
  },
  heroInner: {
    maxWidth: '880px',
    margin: '0 auto',
  },
  label: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.18em',
    color: '#e8622d',
    display: 'block',
    marginBottom: '20px',
  },
  title: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.5rem, 3.2vw, 2.2rem)',
    fontWeight: 700,
    lineHeight: 1.4,
    color: '#1c1917',
    margin: 0,
  },

  /* Steps */
  steps: { padding: '30px 8% 70px' },
  stepsGrid: {
    maxWidth: '880px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: '44px 56px',
  },
  step: {},
  stepTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '16px',
  },
  stepNum: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '2.2rem',
    fontWeight: 700,
    color: '#e8622d',
    lineHeight: 1,
    flexShrink: 0,
  },
  stepLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#d6d3d1',
  },
  stepTitle: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.05rem',
    fontWeight: 700,
    marginBottom: '8px',
    marginTop: 0,
    color: '#1c1917',
  },
  stepDesc: {
    fontSize: '0.95rem',
    color: '#57534e',
    lineHeight: 1.7,
    margin: 0,
  },

  /* Technical section */
  tech: { padding: '0 8% 70px' },
  techBox: {
    maxWidth: '880px',
    margin: '0 auto',
    backgroundColor: '#1c1917',
    borderRadius: '4px',
    padding: '44px 48px',
  },
  techLabel: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.18em',
    color: '#e8622d',
    display: 'block',
    marginBottom: '20px',
  },
  techText: {
    fontSize: '1rem',
    color: '#d6d3d1',
    lineHeight: 1.8,
    marginTop: 0,
    marginBottom: '14px',
  },

  /* CTA */
  cta: {
    padding: '70px 8%',
    textAlign: 'center',
    borderTop: '1px solid #e7e5e4',
  },
  ctaTitle: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.3rem, 2.5vw, 1.7rem)',
    fontWeight: 700,
    marginTop: 0,
    marginBottom: '24px',
    color: '#1c1917',
  },
  ctaBtn: {
    fontFamily: '"Space Mono", monospace',
    padding: '13px 34px',
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#ffffff',
    backgroundColor: '#e8622d',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    letterSpacing: '0.02em',
  },

  /* Footer */
  footer: {
    padding: '36px 8%',
    textAlign: 'center',
    borderTop: '1px solid #e7e5e4',
  },
  footerRow: { marginBottom: '10px' },
  dot: { margin: '0 10px', color: '#a8a29e' },
  copy: { color: '#a8a29e', fontSize: '0.8rem', margin: 0 },
};

/* Load fonts */
if (!document.querySelector('#bracketsim-fonts')) {
  const link = document.createElement('link');
  link.id = 'bracketsim-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap';
  document.head.appendChild(link);
}

/* Interaction styles */
if (!document.querySelector('#how-it-works-styles')) {
  const sheet = document.createElement('style');
  sheet.id = 'how-it-works-styles';
  sheet.textContent = `
    .hiw-cta:hover { background-color: #c2410c !important; }
    .hiw-login-link:hover {
      background-color: #e8622d !important;
      color: #fff !important;
    }
    .hiw-flink {
      color: #78716c;
      text-decoration: none;
      font-size: 0.9rem;
    }
    .hiw-flink:hover { color: #e8622d; }
    .hiw-step {
      opacity: 0;
      animation: hiwFadeUp 0.5s ease-out forwards;
    }
    .hiw-step:nth-child(2) { animation-delay: 0.08s; }
    .hiw-step:nth-child(3) { animation-delay: 0.16s; }
    .hiw-step:nth-child(4) { animation-delay: 0.24s; }
    @keyframes hiwFadeUp {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(sheet);
}

export default HowItWorks;
